import { Trans, useLingui } from '@lingui/react/macro';
import { differenceInBusinessDays } from 'date-fns/differenceInBusinessDays';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCalendar from '@/icons/Calendar.svg';
import IconCalendarOverdue from '@/icons/CalendarOverdue.svg';
import IconCalendarReminder from '@/icons/CalendarReminder.svg';
import { formatDate } from '@/utils/date';

export const StatusDueDate = ({
  date,
  variant,
  displayDayMonthOnly,
}: {
  date: Date;
  variant: 'info' | 'row' | 'card' | 'small';
  displayDayMonthOnly?: boolean;
}) => {
  const { t } = useLingui();
  const label = t({ id: 'components.status-due-date.label', message: 'Status due date' });
  const dayLabel = t({ id: 'components.status-due-date.day-label', message: 'd' });

  const referenceDate = new Date();

  referenceDate.setUTCHours(18, 0, 0, 0);

  const difference = differenceInBusinessDays(referenceDate, date);
  const isDueToday = difference === 0;
  const isOverdue = difference > 0;

  const isSmall = variant === 'small';
  const iconSize = isSmall ? '16px' : '1rem';
  const outerGap = isSmall ? '4px' : variant === 'info' ? '0.25rem' : '0.5rem';
  const innerGap = isSmall ? '4px' : '0.25rem';
  const textSize = isSmall || variant === 'card' ? '200' : '100';
  const textWeight = variant === 'info' ? 'medium' : 'regular';

  const content = (
    <Stack>
      {variant === 'info' && (
        <InlineText size="200" weight="regular" color="secondary">
          {label}
        </InlineText>
      )}
      <Stack row gap={outerGap} alignItems="center" flexWrap="nowrap">
        {isDueToday ? (
          <IconCalendarReminder className="text-warning" style={{ fontSize: iconSize }} />
        ) : isOverdue ? (
          <IconCalendarOverdue className="text-danger" style={{ fontSize: iconSize }} />
        ) : (
          <IconCalendar style={{ fontSize: iconSize }} />
        )}
        <Stack row gap={innerGap} alignItems="center" flexWrap="nowrap">
          <InlineText
            size={textSize}
            weight={textWeight}
            color={isDueToday ? 'warning' : isOverdue ? 'danger' : undefined}
            style={{ whiteSpace: 'nowrap' }}
            aria-label={label}
          >
            {formatDate(
              date,
              displayDayMonthOnly ? { day: 'numeric', month: 'short' } : { dateStyle: 'medium' }
            )}
          </InlineText>
          {isOverdue && (
            <InlineText size="200" weight="medium" color="danger">
              {`(+${difference}${dayLabel})`}
            </InlineText>
          )}
        </Stack>
      </Stack>
    </Stack>
  );

  if (variant === 'card') {
    return (
      <Box padding="0.25rem 0.5rem" style={{ flex: 'none' }}>
        {content}
      </Box>
    );
  }

  return content;
};

export const RequestDueDate = ({
  date,
  estimatedDate,
  variant,
  displayDayMonthOnly,
}: {
  date: Date | null;
  estimatedDate: Date | null;
  variant: 'info' | 'row' | 'card';
  displayDayMonthOnly?: boolean;
}) => {
  const { t } = useLingui();
  const label = t({ id: 'components.request-due-date.label', message: 'Initial due date' });
  const dayLabel = t({ id: 'components.request-due-date.day-label', message: 'd' });

  const difference = date && estimatedDate ? differenceInBusinessDays(estimatedDate, date) : 0;
  const isInAdvance = difference < 0;
  const isOverdue = difference > 0;

  if (!date) {
    return null;
  }

  return (
    <Stack>
      {variant === 'info' && (
        <Paragraph color="secondary" size="200">
          {label}
        </Paragraph>
      )}
      <Stack
        row
        gap={variant === 'row' ? '0.5rem' : '0.25rem'}
        alignItems="center"
        flexWrap="nowrap"
      >
        {isOverdue ? (
          <IconCalendarOverdue className="text-danger" style={{ fontSize: '1rem' }} />
        ) : isInAdvance ? (
          <IconCalendar className="color-primary-800" style={{ fontSize: '1rem' }} />
        ) : (
          <IconCalendar style={{ fontSize: '1rem' }} />
        )}
        <Stack row gap="0.25rem" alignItems="center">
          <Paragraph
            size={variant === 'info' ? '100' : variant === 'card' ? '200' : '100'}
            weight={variant === 'info' || variant === 'card' ? 'medium' : 'regular'}
            color={isOverdue ? 'danger' : isInAdvance ? 'success' : 'primary'}
            aria-label={label}
          >
            {formatDate(
              date,
              displayDayMonthOnly ? { day: 'numeric', month: 'short' } : { dateStyle: 'medium' }
            )}
          </Paragraph>
          {!!estimatedDate &&
            (isInAdvance || isOverdue) &&
            (variant === 'info' ? (
              <Paragraph size="200" weight="medium" color={isOverdue ? 'danger' : 'success'}>
                {isOverdue ? `(+${difference}${dayLabel})` : `(${difference}${dayLabel})`}
              </Paragraph>
            ) : (
              <Tooltip
                content={
                  <span>
                    <Trans id="components.request-due-date.tooltip">
                      Estimated completion date:
                    </Trans>{' '}
                    {formatDate(
                      estimatedDate,
                      displayDayMonthOnly
                        ? { day: 'numeric', month: 'short' }
                        : { dateStyle: 'medium' }
                    )}
                  </span>
                }
              >
                <Button variant="style-less">
                  <InlineText size="200" weight="medium" color={isOverdue ? 'danger' : 'success'}>
                    {isOverdue ? `(+${difference}${dayLabel})` : `(${difference}${dayLabel})`}
                  </InlineText>
                </Button>
              </Tooltip>
            ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
